import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PROJECT_STATUS,
  PROJECT_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
} from '@/constants/project';
import { ROUTES } from '@/constants/routes';
import { ProjectDepartmentMigrationModal } from '@/pages/admin/projects/ProjectDepartmentMigrationModal';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectListItem, ProjectStatus } from '@/types/project';
import { formatDate } from '@/utils/format-date';

interface FilterFormValues {
  keyword?: string;
}

function isPendingMigration(project: ProjectListItem): boolean {
  return project.departmentsConfigured === false;
}

export function ProjectListPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [filterForm] = Form.useForm<FilterFormValues>();

  const [statusTab, setStatusTab] = useState<ProjectStatus>(PROJECT_STATUS.ACTIVE);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterFormValues>({});

  const [migrationOpen, setMigrationOpen] = useState(false);
  const [migrationProject, setMigrationProject] = useState<ProjectListItem | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const result = await projectsApi.fetchProjects({
        page,
        pageSize,
        keyword: filters.keyword?.trim() || undefined,
        status: statusTab,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目列表失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [filters, message, page, pageSize, statusTab]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const pendingCount = useMemo(
    () => items.filter((item) => isPendingMigration(item)).length,
    [items],
  );

  const handleSearch = (values: FilterFormValues) => {
    setFilters(values);
    setPage(1);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 20);
  };

  const openMigration = (record: ProjectListItem) => {
    setMigrationProject(record);
    setMigrationOpen(true);
  };

  const columns: ColumnsType<ProjectListItem> = [
    {
      title: '项目编号',
      dataIndex: 'projectNo',
      width: 140,
      render: (value: string, record) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => navigate(ROUTES.admin.projectDetail(record.id))}
        >
          {value}
        </Button>
      ),
    },
    { title: '项目名称', dataIndex: 'name', ellipsis: true },
    {
      title: '项目类型',
      dataIndex: 'projectType',
      width: 110,
      render: (value: ProjectListItem['projectType']) => PROJECT_TYPE_LABELS[value] ?? value,
    },
    {
      title: '项目负责人',
      dataIndex: ['projectManager', 'name'],
      width: 120,
      render: (_: unknown, record) => record.projectManager?.name ?? '-',
    },
    {
      title: '部门配置',
      width: 100,
      render: (_: unknown, record) =>
        isPendingMigration(record) ? (
          <Tag color="warning">待迁移</Tag>
        ) : (
          <Tag color="success">已配置</Tag>
        ),
    },
    {
      title: '项目状态',
      dataIndex: 'status',
      width: 100,
      render: (value: ProjectStatus) => (
        <Tag color={value === PROJECT_STATUS.ACTIVE ? 'processing' : 'default'}>
          {PROJECT_STATUS_LABELS[value]}
        </Tag>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'startDate',
      width: 120,
      render: (value: string | null) => formatDate(value),
    },
    {
      title: '结束时间',
      dataIndex: 'endDate',
      width: 120,
      render: (value: string | null) => formatDate(value),
    },
    {
      title: '成员数',
      width: 80,
      align: 'center',
      render: (_: unknown, record) => record.memberCount ?? record._count?.members ?? 0,
    },
    {
      title: '操作',
      width: 160,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Space size={0} wrap>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(ROUTES.admin.projectDetail(record.id))}
          >
            详情
          </Button>
          {isPendingMigration(record) ? (
            <Button type="link" size="small" onClick={() => openMigration(record)}>
              配置部门
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        项目管理
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        通过项目部门自动计算成员；历史项目需先完成部门迁移确认。
      </Typography.Paragraph>

      {statusTab === PROJECT_STATUS.ACTIVE && pendingCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`当前页有 ${pendingCount} 个项目待配置项目部门`}
          description="请使用「配置部门」根据系统建议确认部门范围，确认后将自动同步项目成员。"
        />
      ) : null}

      <Card style={{ marginBottom: 16 }}>
        <Tabs
          activeKey={statusTab}
          onChange={(key) => {
            setStatusTab(key as ProjectStatus);
            setPage(1);
          }}
          items={[
            { key: PROJECT_STATUS.ACTIVE, label: '进行中' },
            { key: PROJECT_STATUS.ARCHIVED, label: '归档库' },
          ]}
        />
        <Form form={filterForm} layout="inline" onFinish={handleSearch} style={{ marginTop: 8 }}>
          <Form.Item name="keyword" label="关键字">
            <Input allowClear placeholder="项目编号 / 项目名称" style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields();
                  setFilters({});
                  setPage(1);
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="项目列表"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(ROUTES.admin.projectCreate)}>
            创建项目
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <ProjectDepartmentMigrationModal
        open={migrationOpen}
        project={migrationProject}
        onClose={() => {
          setMigrationOpen(false);
          setMigrationProject(null);
        }}
        onConfirmed={() => void loadProjects()}
      />
    </div>
  );
}
